import * as THREE from 'three';
import type { FeatureResultMesh } from './FeatureResultMesh';
import { FeatureLine } from './FeatureLine';
import { createFeatureFaceMaterial, FeatureFaceMaterialState } from '../../material';/** 特征面默认命名计数器。 */
let featureFaceNameCounter = 1;

/**
 * 判断对象是否为特征面代理。
 *
 * @param value 待判断对象。
 * @returns 是否为特征面代理。
 */
export function isFeatureFace(value: unknown): value is FeatureFace {
  if (!value || typeof value !== 'object') {
    return false;
  }
  return Reflect.get(value, 'isFeatureFace') === true;
}

/**
 * 特征面代理对象。
 *
 * 设计说明：
 * 1. 面代理对象是单面选择、单面高亮、边界线联动的统一语义承载；
 * 2. 相邻边索引、元数据等语义直接保存在对象成员上；
 * 3. 该对象不依赖任何隐式标签作为内部真相。
 */
export class FeatureFace extends THREE.Mesh {
  /** 运行时类型标记。 */
  public readonly isFeatureFace: boolean = true;

  /** 所属结果体对象 ID。 */
  public readonly objectId: string;

  /** 面索引。 */
  public readonly faceIndex: number;

  /** 权威子拓扑 ID。 */
  public readonly subEntityId: string;

  /** 持久化面引用。 */
  public readonly persistentRef: string;

  /** 面三角形范围。 */
  public readonly triangleRange: [number, number];

  /** 邻接边索引。 */
  public readonly adjacentEdgeIndices: number[];

  /** 特征元数据。 */
  private featureMetadata: Record<string, unknown> = {};

  /** 权威拓扑快照中的局部面法向。 */
  private readonly authoritativeLocalNormal: THREE.Vector3 | null;

  /** 权威拓扑快照中的局部面代表点。 */
  private readonly authoritativeLocalPoint: THREE.Vector3 | null;

  /** 权威拓扑曲面类型；缺失时需要由本实例的完整索引几何验证平面性。 */
  public readonly surfaceKind: TopologySurfaceKind | undefined;

  /** 缺失曲面类型时惰性缓存的局部几何平面；新拓扑实例会自然失效。 */
  private fallbackLocalPlane: THREE.Plane | null | undefined;

  /** 本实例索引几何的惰性局部中心缓存。 */
  private fallbackLocalCenter: THREE.Vector3 | null | undefined;

  /** 旧快照平面验证的缓存承载对象。 */
  private readonly fallbackPlaneScratch = new THREE.Plane();

  /** 旧快照平面验证复用的局部向量，避免首次重算按顶点创建对象。 */
  private readonly fallbackFirst = new THREE.Vector3();
  /** 旧快照平面验证复用的局部向量。 */
  private readonly fallbackSecond = new THREE.Vector3();
  /** 旧快照平面验证复用的局部向量。 */
  private readonly fallbackThird = new THREE.Vector3();
  /** 旧快照平面验证复用的边向量。 */
  private readonly fallbackEdgeA = new THREE.Vector3();
  /** 旧快照平面验证复用的边向量。 */
  private readonly fallbackEdgeB = new THREE.Vector3();
  /** 旧快照平面验证复用的法向。 */
  private readonly fallbackNormal = new THREE.Vector3();
  /** 旧快照平面验证复用的顶点。 */
  private readonly fallbackVertex = new THREE.Vector3();
  /** 旧快照面中心计算复用的包围盒。 */
  private readonly fallbackBounds = new THREE.Box3();

  /** 高频平面读取复用的法向矩阵。 */
  private readonly worldPlaneNormalMatrixScratch = new THREE.Matrix3();

  /** 所属权威结果体。*/
  private owningResultMesh: FeatureResultMesh | null = null;

  /** 当前面对应的边界线对象集合。 */
  private boundaryLines: FeatureLine[] = [];

  /**
   * 从拓扑快照三元组创建局部向量。
   *
   * @param value 拓扑快照中的局部坐标或法向。
   * @param shouldRequireNonZero 是否要求向量长度非零。
   * @returns 可用局部向量；数据缺失或包含非法数值时返回 `null`。
   */
  private static createLocalVectorFromTopologyTuple(
    value: TopologyPoint3D | undefined,
    shouldRequireNonZero = false,
  ): THREE.Vector3 | null {
    if (!Array.isArray(value) || value.length < 3) {
      return null;
    }
    const vector = new THREE.Vector3(value[0], value[1], value[2]);
    if (!Number.isFinite(vector.x) || !Number.isFinite(vector.y) || !Number.isFinite(vector.z)) {
      return null;
    }
    if (shouldRequireNonZero && vector.lengthSq() <= 1e-12) {
      return null;
    }
    return vector;
  }

  //------ 参考平面面法向读取修复 开始------
  /**
   * 读取指定顶点索引对应的位置。
   *
   * @param positionAttribute 顶点位置属性。
   * @param vertexIndex 顶点索引。
   * @returns 顶点位置；索引越界时返回 null。
   */
  private readVertexPosition(
    positionAttribute: THREE.BufferAttribute,
    vertexIndex: number
  ): THREE.Vector3 | null {
    if (vertexIndex < 0 || vertexIndex >= positionAttribute.count) {
      return null;
    }
    return new THREE.Vector3(
      positionAttribute.getX(vertexIndex),
      positionAttribute.getY(vertexIndex),
      positionAttribute.getZ(vertexIndex)
    );
  }

  /**
   * 从当前面代理的索引三角形中计算局部法向。
   *
   * 设计说明：
   * 1. FeatureFace 代理几何会复用结果体母几何的 position/normal 属性；
   * 2. 仅通过 `normal[0]` 读取法向会错误命中母几何的第一个顶点；
   * 3. 这里必须基于当前代理面自己的索引三角形计算真实面法向，才能保证任意被选中的面都沿自身法线偏移。
   *
   * @returns 局部法向；无法计算时返回 null。
   */
  private computeIndexedFaceLocalNormal(): THREE.Vector3 | null {
    const positionAttribute = this.geometry.getAttribute('position');
    if (!(positionAttribute instanceof THREE.BufferAttribute)) {
      return null;
    }

    const indexAttribute = this.geometry.getIndex();
    const vertexCount = indexAttribute instanceof THREE.BufferAttribute
      ? indexAttribute.count
      : positionAttribute.count;

    for (let cursor = 0; cursor + 2 < vertexCount; cursor += 3) {
      const firstIndex = indexAttribute instanceof THREE.BufferAttribute ? Number(indexAttribute.getX(cursor)) : cursor;
      const secondIndex = indexAttribute instanceof THREE.BufferAttribute ? Number(indexAttribute.getX(cursor + 1)) : cursor + 1;
      const thirdIndex = indexAttribute instanceof THREE.BufferAttribute ? Number(indexAttribute.getX(cursor + 2)) : cursor + 2;

      const first = this.readVertexPosition(positionAttribute, firstIndex);
      const second = this.readVertexPosition(positionAttribute, secondIndex);
      const third = this.readVertexPosition(positionAttribute, thirdIndex);
      if (!first || !second || !third) {
        continue;
      }

      const edgeA = second.clone().sub(first);
      const edgeB = third.clone().sub(first);
      const normal = edgeA.cross(edgeB);
      if (normal.lengthSq() > 1e-12) {
        return normal.normalize();
      }
    }

    return null;
  }

  /**
   * 从完整索引几何验证并缓存局部平面。
   *
   * 单个三角形只能提供局部切平面，不能证明整个 CAD 面为平面；因此旧快照缺失 surfaceKind 时，
   * 必须先找到非退化参考三角形，再验证全部索引顶点到该平面的距离。
   *
   * @returns 全部顶点共面时返回实例内缓存平面；非共面或几何非法时返回 null。
   */
  private resolveFallbackLocalPlane(): THREE.Plane | null {
    if (this.fallbackLocalPlane !== undefined) {
      return this.fallbackLocalPlane;
    }
    const positionAttribute = this.geometry.getAttribute('position');
    if (!(positionAttribute instanceof THREE.BufferAttribute)) {
      this.fallbackLocalPlane = null;
      return null;
    }

    const indexAttribute = this.geometry.getIndex();
    const vertexCount = indexAttribute instanceof THREE.BufferAttribute
      ? indexAttribute.count
      : positionAttribute.count;

    let hasReferenceTriangle = false;
    for (let cursor = 0; cursor + 2 < vertexCount; cursor += 3) {
      const firstIndex = indexAttribute instanceof THREE.BufferAttribute ? Number(indexAttribute.getX(cursor)) : cursor;
      const secondIndex = indexAttribute instanceof THREE.BufferAttribute ? Number(indexAttribute.getX(cursor + 1)) : cursor + 1;
      const thirdIndex = indexAttribute instanceof THREE.BufferAttribute ? Number(indexAttribute.getX(cursor + 2)) : cursor + 2;
      if (
        !this.copyVertexPositionTo(positionAttribute, firstIndex, this.fallbackFirst)
        || !this.copyVertexPositionTo(positionAttribute, secondIndex, this.fallbackSecond)
        || !this.copyVertexPositionTo(positionAttribute, thirdIndex, this.fallbackThird)
      ) {
        continue;
      }
      this.fallbackEdgeA.subVectors(this.fallbackSecond, this.fallbackFirst);
      this.fallbackEdgeB.subVectors(this.fallbackThird, this.fallbackFirst);
      this.fallbackNormal.crossVectors(this.fallbackEdgeA, this.fallbackEdgeB);
      if (this.fallbackNormal.lengthSq() > 1e-12) {
        this.fallbackNormal.normalize();
        hasReferenceTriangle = true;
        break;
      }
    }
    if (!hasReferenceTriangle) {
      this.fallbackLocalPlane = null;
      return null;
    }

    const plane = this.fallbackPlaneScratch.setFromNormalAndCoplanarPoint(
      this.fallbackNormal,
      this.fallbackFirst,
    );
    if (this.surfaceKind === 'plane') {
      this.fallbackLocalPlane = plane;
      return plane;
    }
    for (let cursor = 0; cursor < vertexCount; cursor += 1) {
      const vertexIndex = indexAttribute instanceof THREE.BufferAttribute
        ? Number(indexAttribute.getX(cursor))
        : cursor;
      if (
        !this.copyVertexPositionTo(positionAttribute, vertexIndex, this.fallbackVertex)
        || Math.abs(plane.distanceToPoint(this.fallbackVertex)) > 1e-6
      ) {
        this.fallbackLocalPlane = null;
        return null;
      }
    }
    this.fallbackLocalPlane = plane;
    return plane;
  }

  /**
   * 从当前面代理的索引顶点中计算局部中心点。
   *
   * 设计说明：
   * 1. FeatureFace 是结果体的子节点，节点原点通常等于整个结果体原点；
   * 2. 单面偏移距离为 0 时，参考平面必须经过被选中的那张面；
   * 3. 因此代表点不能取对象原点，必须取当前索引子集对应面的局部中心。
   *
   * @returns 当前面的局部中心点；无法计算时返回 null。
   */
  private computeIndexedFaceLocalCenter(): THREE.Vector3 | null {
    if (this.fallbackLocalCenter !== undefined) {
      return this.fallbackLocalCenter;
    }
    const positionAttribute = this.geometry.getAttribute('position');
    if (!(positionAttribute instanceof THREE.BufferAttribute)) {
      this.fallbackLocalCenter = null;
      return null;
    }

    const indexAttribute = this.geometry.getIndex();
    const vertexCount = indexAttribute instanceof THREE.BufferAttribute
      ? indexAttribute.count
      : positionAttribute.count;
    if (vertexCount <= 0) {
      this.fallbackLocalCenter = null;
      return null;
    }

    const box = this.fallbackBounds.makeEmpty();
    let hasPoint = false;
    for (let cursor = 0; cursor < vertexCount; cursor += 1) {
      const vertexIndex = indexAttribute instanceof THREE.BufferAttribute
        ? Number(indexAttribute.getX(cursor))
        : cursor;
      if (!this.copyVertexPositionTo(positionAttribute, vertexIndex, this.fallbackVertex)) {
        continue;
      }
      box.expandByPoint(this.fallbackVertex);
      hasPoint = true;
    }

    if (!hasPoint || box.isEmpty()) {
      this.fallbackLocalCenter = null;
      return null;
    }
    this.fallbackLocalCenter = box.getCenter(new THREE.Vector3());
    return this.fallbackLocalCenter;
  }

  /**
   * 把指定局部顶点写入调用方复用向量。
   * @param positionAttribute 顶点位置属性。
   * @param vertexIndex 顶点索引。
   * @param output 复用输出向量。
   * @returns 索引有效时返回 true。
   */
  private copyVertexPositionTo(
    positionAttribute: THREE.BufferAttribute,
    vertexIndex: number,
    output: THREE.Vector3,
  ): boolean {
    if (vertexIndex < 0 || vertexIndex >= positionAttribute.count) {
      return false;
    }
    output.set(
      positionAttribute.getX(vertexIndex),
      positionAttribute.getY(vertexIndex),
      positionAttribute.getZ(vertexIndex),
    );
    return true;
  }
  //------ 参考平面面法向读取修复 结束------

  /**
   * 构造函数。
   *
   * @param input 输入参数。
   */
  public constructor(input: {
    objectId: string;
    faceIndex: number;
    subEntityId: string;
    persistentRef: string;
    triangleRange: [number, number];
    adjacentEdgeIndices: number[];
    geometry: THREE.BufferGeometry;
    localNormal?: TopologyPoint3D;
    localPoint?: TopologyPoint3D;
    surfaceKind?: TopologySurfaceKind;
  }) {
    super(input.geometry, createFeatureFaceMaterial(FeatureFaceMaterialState.Default));
    this.objectId = input.objectId;
    this.faceIndex = input.faceIndex;
    this.subEntityId = input.subEntityId;
    this.persistentRef = input.persistentRef;
    this.triangleRange = [input.triangleRange[0], input.triangleRange[1]];
    this.adjacentEdgeIndices = [...input.adjacentEdgeIndices];
    this.authoritativeLocalNormal = FeatureFace.createLocalVectorFromTopologyTuple(input.localNormal, true);
    this.authoritativeLocalPoint = FeatureFace.createLocalVectorFromTopologyTuple(input.localPoint);
    this.surfaceKind = input.surfaceKind;
    this.name = `面${featureFaceNameCounter}`;
    featureFaceNameCounter += 1;
    this.renderOrder = 1;
    this.frustumCulled = false;
  }

  /**
   * 设置特征面元数据。
   *
   * @param metadata 元数据。
   */
  public setFeatureMetadata(metadata: Record<string, unknown>): void {
    this.featureMetadata = {
      ...metadata,
    };
  }

  /**
   * 读取特征元数据副本。
   *
   * @returns 元数据副本。
   */
  public getFeatureMetadata(): Record<string, unknown> {
    return {
      ...this.featureMetadata,
    };
  }

  /**
   * 设置当前面代理所属的权威结果体。
   *
   * @param mesh 所属结果体；传入 `null` 表示清空绑定。
   */
  public setOwningResultMesh(mesh: FeatureResultMesh | null): void {
    this.owningResultMesh = mesh;
  }

  /**
   * 读取当前面代理所属的权威结果体。
   *
   * @returns 所属结果体；未绑定时返回 `null`。
   */
  public getOwningResultMesh(): FeatureResultMesh | null {
    return this.owningResultMesh;
  }

  /**
   * 绑定当前面的边界线对象。
   *
   * @param lines 邻接边界线。
   */
  public setBoundaryLines(lines: FeatureLine[]): void {
    this.boundaryLines = [...lines];
  }

  /**
   * 获取当前面的边界线对象快照。
   *
   * @returns 边界线对象数组。
   */
  public getBoundaryLines(): FeatureLine[] {
    return [...this.boundaryLines];
  }

  //------ 参考平面几何读取封装 开始------
  /**
   * 读取特征面中心点（世界坐标）。
   *
   * 当前特征面在参考平面求解中以对象世界位置作为代表点，
   * 由面对象自身提供统一入口，避免上层重复处理。
   *
   * @returns 世界坐标。
   */
  public getWorldPoint(): THREE.Vector3 {
    //------ 参考平面面法向读取修复 开始------
    this.updateWorldMatrix(true, false);
    if (this.authoritativeLocalPoint) {
      return this.authoritativeLocalPoint.clone().applyMatrix4(this.matrixWorld);
    }
    const localCenter = this.computeIndexedFaceLocalCenter();
    if (localCenter) {
      // 局部中心按拓扑实例缓存；世界坐标读取必须复制后变换，不能污染后续平面热路径的局部基线。
      return localCenter.clone().applyMatrix4(this.matrixWorld);
    }
    const point = new THREE.Vector3();
    this.getWorldPosition(point);
    return point;
    //------ 参考平面面法向读取修复 结束------
  }

  /**
   * 读取特征面平面参数（世界坐标）。
   *
   * 优先使用几何法向属性的首个法向，缺失时退回对象世界朝向的 Z 轴。
   *
   * @returns 世界法向与代表点。
   */
  public getWorldPlane(): PlanePointNormalGeometry {
    this.updateWorldMatrix(true, false);
    let worldNormal: THREE.Vector3;
    //------ 参考平面面法向读取修复 开始------
    const localNormal = this.authoritativeLocalNormal?.clone() ?? this.computeIndexedFaceLocalNormal();
    if (localNormal) {
      worldNormal = localNormal.applyMatrix3(new THREE.Matrix3().getNormalMatrix(this.matrixWorld)).normalize();
    } else {
      worldNormal = new THREE.Vector3(0, 0, 1).applyQuaternion(this.getWorldQuaternion(new THREE.Quaternion())).normalize();
    }
    //------ 参考平面面法向读取修复 结束------
    const worldPoint = this.getWorldPoint();
    //------ 偏移基准面外法向校正 开始------
    /**
     * 偏移基准面方向语义要求“正值 = 向外”。
     *
     * 对结果体上的面代理来说，单纯依赖三角形 winding 仍可能得到与实体外侧相反的法向。
     * 因此这里额外使用父结果体包围盒中心做一次朝外校正：
     * 1. 从结果体中心指向当前面的向量，近似代表“外侧方向”；
     * 2. 若该方向与当前法向点乘为负，则翻转法向；
     * 3. 当父对象不是结果体，或中心退化时，保持当前法向不变。
     *
     * 注意：该校正对所有在 FeatureResultMesh 上的面执行，不再区分 authoritativeLocalNormal
     * 是否来自 Puppy CAD 内核 拓扑。因为 Puppy CAD 内核 布尔操作（相切合并/相交合并）可能改变 B-rep face 的
     * orientation 标志，导致三角化 winding 反转，而包围盒中心到面中心的空间关系在布尔
     * 操作前后是稳定的。
     */
    const parentObject = this.parent;
    if (parentObject && Reflect.get(parentObject, 'isFeatureResultMesh') === true) {
      const parentBounds = new THREE.Box3().setFromObject(parentObject);
      if (!parentBounds.isEmpty()) {
        const parentCenter = parentBounds.getCenter(new THREE.Vector3());
        const outwardDirection = worldPoint.clone().sub(parentCenter);
        if (outwardDirection.lengthSq() > 1e-12 && outwardDirection.dot(worldNormal) < 0) {
          worldNormal = worldNormal.clone().multiplyScalar(-1);
        }
      }
    }
    //------ 偏移基准面外法向校正 结束------
    return {
      normal: worldNormal,
      point: worldPoint,
    };
  }

  /**
   * 把当前面的世界平面写入调用方复用向量。
   *
   * 镜像轴等高频关系只关心无向几何平面，因此无需执行包围盒外法向校正；法向整体翻转不会改变平面或交线。
   * 权威拓扑提供局部法向与代表点时，本入口不会创建中间向量、矩阵或结果对象。
   *
   * @param normalOut 承载单位世界法向的复用向量。
   * @param pointOut 承载世界代表点的复用向量。
   * @returns 可得到非退化平面时返回 true。
   */
  public copyWorldPlaneTo(normalOut: THREE.Vector3, pointOut: THREE.Vector3): boolean {
    if (this.surfaceKind !== undefined && this.surfaceKind !== 'plane') {
      return false;
    }
    this.updateWorldMatrix(true, false);
    const fallbackPlane = (
      this.surfaceKind === undefined
      || !this.authoritativeLocalNormal
      || !this.authoritativeLocalPoint
    )
      ? this.resolveFallbackLocalPlane()
      : null;
    if (this.surfaceKind === undefined && !fallbackPlane) {
      return false;
    }
    const localNormal = this.authoritativeLocalNormal ?? fallbackPlane?.normal ?? null;
    if (!localNormal) {
      return false;
    }
    normalOut.copy(localNormal)
      .applyMatrix3(this.worldPlaneNormalMatrixScratch.getNormalMatrix(this.matrixWorld))
      .normalize();

    if (this.authoritativeLocalPoint) {
      pointOut.copy(this.authoritativeLocalPoint).applyMatrix4(this.matrixWorld);
    } else if (fallbackPlane) {
      const localCenter = this.computeIndexedFaceLocalCenter();
      if (!localCenter) {
        return false;
      }
      pointOut.copy(localCenter).applyMatrix4(this.matrixWorld);
    } else {
      const localCenter = this.computeIndexedFaceLocalCenter();
      if (localCenter) {
        pointOut.copy(localCenter).applyMatrix4(this.matrixWorld);
      } else {
        this.getWorldPosition(pointOut);
      }
    }
    return normalOut.lengthSq() > 1e-12
      && Number.isFinite(pointOut.x)
      && Number.isFinite(pointOut.y)
      && Number.isFinite(pointOut.z);
  }

  /**
   * 判断当前面是否具备完整平面语义。
   * @returns 权威类型为 plane，或旧快照完整索引几何经一次性共面验证时返回 true。
   */
  public isPlanarGeometry(): boolean {
    if (this.surfaceKind !== undefined) {
      return this.surfaceKind === 'plane';
    }
    return this.resolveFallbackLocalPlane() !== null;
  }

  /**
   * 读取特征面边界顶点（世界坐标）。
   *
   * 通过边界线对象统一提取端点，保证边界读取逻辑集中在特征对象层。
   *
   * @returns 边界顶点序列。
   */
  public getWorldBoundaryVertices(): THREE.Vector3[] {
    this.updateWorldMatrix(true, false);
    const vertices: THREE.Vector3[] = [];
    let lines = this.getBoundaryLines();
    if (lines.length === 0) {
      this.owningResultMesh?.rebindFeatureFaceBoundaryLines(this);
      lines = this.getBoundaryLines();
    }
    lines.forEach((line) => {
      const endpoints = line.getWorldEndpoints();
      if (!endpoints) {
        return;
      }
      vertices.push(endpoints.start.clone(), endpoints.end.clone());
    });

    if (vertices.length > 0) {
      return vertices;
    }

    /**
     * 拖动装配体零件后的拓扑代理在个别时序下可能尚未补回边界线对象，
     * 但当前面代理自身的索引几何仍然有效。
     *
      * 双面建轴的显示长度依赖面外环范围；若重新绑定真实边界线后仍为空，
      * 上层求解会失去长度来源并把基准轴压缩为单点。
      * 因此这里保留索引几何顶点兜底，至少保证求解器还能得到一个稳定范围。
     */
    const positionAttribute = this.geometry.getAttribute('position');
    if (!(positionAttribute instanceof THREE.BufferAttribute)) {
      return vertices;
    }

    const indexAttribute = this.geometry.getIndex();
    const vertexCount = indexAttribute instanceof THREE.BufferAttribute
      ? indexAttribute.count
      : positionAttribute.count;
    const dedupeKeys = new Set<string>();
    for (let cursor = 0; cursor < vertexCount; cursor += 1) {
      const vertexIndex = indexAttribute instanceof THREE.BufferAttribute
        ? Number(indexAttribute.getX(cursor))
        : cursor;
      const localPoint = this.readVertexPosition(positionAttribute, vertexIndex);
      if (!localPoint) {
        continue;
      }
      const worldPoint = localPoint.applyMatrix4(this.matrixWorld);
      const key = `${worldPoint.x.toFixed(9)}|${worldPoint.y.toFixed(9)}|${worldPoint.z.toFixed(9)}`;
      if (dedupeKeys.has(key)) {
        continue;
      }
      dedupeKeys.add(key);
      vertices.push(worldPoint);
    }
    return vertices;
  }
  //------ 参考平面几何读取封装 结束------
}
